import {AlignmentType, BLOCK_STYLE_ALIGN_LEFT} from "../../components/JourneyMapEditor/CellEditor/EditorCommon";
import Lexical, {$isTextNode} from "lexical";
import {$getNearestBlockElementAncestorOrThrow} from "@lexical/utils";
import {LexicalUtil} from "../../utils/LexicalUtil";
import {AlignStyleType, BoldType, ColorStyleType, FontStyleType, SizeStyleType} from "./TextStyleType";


const DEFAULT_FONT_STYLE_NAME = 'sans_serif';
const DEFAULT_SIZE_STYLE_NAME = 'normal';
const DEFAULT_COLOR_STYLE_NAME = '#000000';
const DEFAULT_ALIGN_STYLE = BLOCK_STYLE_ALIGN_LEFT;

export class TextStyle {
    private readonly font: FontStyleType | null;
    private readonly size: SizeStyleType | null;
    private readonly color: ColorStyleType | null;
    private readonly weight: BoldType;
    private readonly align: AlignStyleType | null;

    constructor(style: { font: FontStyleType | null, size: SizeStyleType | null, color: ColorStyleType | null, weight: BoldType, align: AlignStyleType | null }) {
        this.font = style.font;
        this.size = style.size;
        this.color = style.color;
        this.weight = style.weight;
        this.align = style.align;
    }

    get FontLabel(): string {
        return this.font ? this.font.toLabel() : '';
    }

    get SizeLabel(): string {
        return this.size ? this.size.toLabel() : '';
    }

    get ColorCode(): string {
        return this.color ? this.color.toLabel() : '';
    }

    get IsBold(): boolean {
        return this.weight.isBold();
    }

    get AlignmentType(): AlignmentType | '' {
        return this.align ? this.align.toAlignmentTypeString() : '';
    }

    and(rhs: TextStyle) {
        return new TextStyle({
            font: this.font === rhs.font ? this.font : null,
            size: this.size === rhs.size ? this.size : null,
            color: this.color === rhs.color ? this.color : null,
            weight: this.weight === rhs.weight ? this.weight : BoldType.normal(),
            align: this.align === rhs.align ? this.align : null,
        });
    }


    private _clone(new_style: { font?: FontStyleType, size?: SizeStyleType, color?: ColorStyleType, weight?: BoldType, align?: AlignStyleType }) {
        return new TextStyle({
            font: new_style.font || this.font,
            size: new_style.size || this.size,
            color: new_style.color || this.color,
            weight: new_style.weight || this.weight,
            align: new_style.align || this.align
        });
    }

    setAlignmentType(align: AlignStyleType) {
        return this._clone({align: align});
    }

    toggleWeight() {
        return this._clone({weight: this.weight === BoldType.normal() ? BoldType.bold() : BoldType.normal()});
    }


    static fromLexicalSelection(root: Lexical.RootNode, selection: Lexical.RangeSelection): TextStyle {

        const text_styles = selection.getNodes().filter((n): n is Lexical.TextNode => $isTextNode(n)).map(n => TextStyle.textNodeToStyle(n))

        const paragraph = selection.getNodes().map(node => $getNearestBlockElementAncestorOrThrow(node)).find(() => true);
        let align: AlignStyleType | null = null;
        if (paragraph) {
            const root_align = AlignStyleType.fromLexicalElementNodeOrDefault(root, null);
            align = AlignStyleType.fromLexicalElementNodeOrDefault(paragraph, root_align);
        }

        function and<T>(list: (T)[]): T | null {
            if (list.length === 0) {
                return null;
            }
            return list.every(i => i === list[0]) ? list[0] : null;
        }

        const _default = TextStyle.createDefault();
        return new TextStyle({
            font: text_styles.length === 0 ? _default.font : and(text_styles.map(t => t.font)),
            size: text_styles.length === 0 ? _default.size : and(text_styles.map(t => t.size)),
            color: text_styles.length === 0 ? _default.color : and(text_styles.map(t => t.color)),
            weight: text_styles.map(t => t.weight).every(t => t.isBold()) ? BoldType.bold() : BoldType.normal(),
            align: align,
        });
    }

    private static textNodeToStyle(node: Lexical.TextNode) {
        const style_map = LexicalUtil.textNodeStyleToMap(node);
        const font = style_map.get('font-family');
        const size = style_map.get('font-size');
        const color = style_map.get('color');

        const _default = TextStyle.createDefault();

        return {
            color: color === undefined ? _default.color : ColorStyleType.fromCssValueOrDefault(color, _default.color),
            font: font === undefined ? _default.font : FontStyleType.fromCssValueOrDefault(font, _default.font),
            size: size === undefined ? _default.size : SizeStyleType.fromCssValueOrDefault(size, _default.size),
            weight: BoldType.fromLexicalTextNode(node)
        };
    }

    static fromLexicalEditorState(editor_state: Lexical.EditorState): TextStyle {

        const text_styles = LexicalUtil.extractTextNodes(editor_state).map(node => TextStyle.textNodeToStyle(node));

        const root_align = AlignStyleType.fromLexicalElementNodeOrDefault(editor_state.read(() => Lexical.$getRoot()), null);
        const paragraph_styles = LexicalUtil.extractParagraphNodes(editor_state).map(node => {
            return AlignStyleType.fromLexicalElementNodeOrDefault(node, root_align)
        });

        if (text_styles.length === 0 && paragraph_styles.length === 0) {
            return TextStyle.createDefault();
        }

        //console.log("text_styles", text_styles, '->', and(text_styles.map(t => t.font)));

        function and<T>(list: (T)[]): T | null {
            return list.every(i => i === list[0]) ? list[0] : null;
        }

        return new TextStyle({
            font: and(text_styles.map(t => t.font)),
            size: and(text_styles.map(t => t.size)),
            color: and(text_styles.map(t => t.color)),
            weight: text_styles.map(t => t.weight).every(t => t.isBold()) ? BoldType.bold() : BoldType.normal(),
            align: and(paragraph_styles),
        });
    }

    static createDefault() {
        return new TextStyle({
            color: ColorStyleType.fromString("color_style-" + DEFAULT_COLOR_STYLE_NAME),
            font: FontStyleType.fromString("font_style-" + DEFAULT_FONT_STYLE_NAME),
            size: SizeStyleType.fromString("size_style-" + DEFAULT_SIZE_STYLE_NAME),
            weight: BoldType.normal(),
            align: AlignStyleType.fromString(DEFAULT_ALIGN_STYLE),
        });
    }

    private static complementDefault(param: { color: ColorStyleType | null; size: SizeStyleType | null; weight: BoldType; align: AlignStyleType | null; font: FontStyleType | null }): TextStyle {
        return new TextStyle({
            color: param.color === null ? ColorStyleType.fromString("color_style-" + DEFAULT_COLOR_STYLE_NAME) : param.color,
            font: param.font === null ? FontStyleType.fromString("font_style-" + DEFAULT_FONT_STYLE_NAME) : param.font,
            size: param.size === null ? SizeStyleType.fromString("size_style-" + DEFAULT_SIZE_STYLE_NAME) : param.size,
            weight: param.weight,
            align: param.align === null ? AlignStyleType.fromString(DEFAULT_ALIGN_STYLE) : param.align,
        });
    }
}
